import type { ShiftItem } from "../interface/shift";

export type HireInboxCounts = {
  aiPending: number;
  preCredentialPending: number;
  shiftNewApplicants: number;
};

export type HireInboxItemKey = keyof HireInboxCounts;

export const HIRE_INBOX_LABELS: Record<
  HireInboxItemKey,
  { short: string; action: string }
> = {
  aiPending: {
    short: "AI shifts",
    action: "Validate AI shifts",
  },
  preCredentialPending: {
    short: "Pre-credential",
    action: "Review pre-credential applications",
  },
  shiftNewApplicants: {
    short: "New applicants",
    action: "Review shift applicants",
  },
};

export function isPreCredentialPendingStatus(status?: string | null): boolean {
  const normalized = String(status || "")
    .trim()
    .toUpperCase();
  return normalized === "PENDING" || normalized === "PENDING_REVIEW";
}

export function sumNewApplicantsFromShiftList(list: unknown[]): number {
  if (!Array.isArray(list)) return 0;

  return list.reduce((acc, raw) => {
    const item = raw as Record<string, unknown>;
    let rowTotal = 0;

    const direct = Number(
      item.applicantsNew ??
        item.includesNew ??
        item.totalNewApplicants ??
        item.newApplicants ??
        0,
    );
    if (Number.isFinite(direct) && direct > 0) {
      rowTotal = direct;
    }

    const shiftDays = item.shiftDays;
    if (Array.isArray(shiftDays)) {
      const dayTotal = shiftDays.reduce((dayAcc, dayRaw) => {
        const day = dayRaw as Record<string, unknown>;
        return (
          dayAcc + Number(day.applicantsNew ?? day.includesNew ?? 0)
        );
      }, 0);
      rowTotal = Math.max(rowTotal, dayTotal);
    }

    return acc + (Number.isFinite(rowTotal) ? rowTotal : 0);
  }, 0);
}

export function sumNewApplicantsFromShiftItems(list: ShiftItem[]): number {
  return sumNewApplicantsFromShiftList(list);
}

export function totalHireInboxCount(counts: HireInboxCounts): number {
  return counts.aiPending + counts.preCredentialPending + counts.shiftNewApplicants;
}

/** Individual AI queue rows — API `total` is batch/ingest groups, not shift rows. */
export function sumAiPendingShiftsFromGroupedApiResponse(
  data: unknown,
): number {
  if (!data || typeof data !== "object") return 0;
  const body = data as {
    list?: unknown[];
    total?: number;
    debugActiveRows?: number;
  };

  if (
    typeof body.debugActiveRows === "number" &&
    Number.isFinite(body.debugActiveRows) &&
    body.debugActiveRows >= 0
  ) {
    return body.debugActiveRows;
  }

  const list = Array.isArray(body.list) ? body.list : [];
  if (list.length === 0) {
    return Number(body.total) || 0;
  }

  return list.reduce((sum, raw) => {
    const row = raw as Record<string, unknown>;
    const shiftCount = Number(row.shiftCount);
    if (Number.isFinite(shiftCount) && shiftCount > 0) {
      return sum + shiftCount;
    }
    if (Array.isArray(row.cards)) {
      return sum + row.cards.length;
    }
    return sum + 1;
  }, 0);
}

export function sumAiPendingShiftsFromShiftList(list: unknown[]): number {
  if (!Array.isArray(list) || list.length === 0) return 0;

  return list.reduce((sum, raw) => {
    const item = raw as Record<string, unknown>;
    const ingestCards = item.aiIngestCards;
    if (Array.isArray(ingestCards) && ingestCards.length > 0) {
      return (
        sum +
        ingestCards.reduce((cardSum, cardRaw) => {
          const card = cardRaw as Record<string, unknown>;
          const groupItems = card.aiGroupItems;
          const fromGroup = Array.isArray(groupItems) ? groupItems.length : 0;
          const fromCount = Number(card.aiShiftCount);
          if (Number.isFinite(fromCount) && fromCount > 0) return cardSum + fromCount;
          if (fromGroup > 0) return cardSum + fromGroup;
          return cardSum + 1;
        }, 0)
      );
    }

    const shiftCount = Number(item.aiShiftCount ?? item.shiftCount);
    if (Number.isFinite(shiftCount) && shiftCount > 0) {
      return sum + shiftCount;
    }
    const groupItems = item.aiGroupItems;
    if (Array.isArray(groupItems) && groupItems.length > 0) {
      return sum + groupItems.length;
    }
    return sum + 1;
  }, 0);
}

export function mergeHireInboxCounts(
  remote: HireInboxCounts,
  overrides?: Partial<HireInboxCounts>,
): HireInboxCounts {
  return {
    aiPending:
      overrides?.aiPending != null ? overrides.aiPending : remote.aiPending,
    preCredentialPending:
      overrides?.preCredentialPending != null
        ? overrides.preCredentialPending
        : remote.preCredentialPending,
    shiftNewApplicants:
      overrides?.shiftNewApplicants != null
        ? overrides.shiftNewApplicants
        : remote.shiftNewApplicants,
  };
}

export function diffHireInboxCounts(
  previous: HireInboxCounts,
  next: HireInboxCounts,
): Partial<HireInboxCounts> {
  const delta: Partial<HireInboxCounts> = {};
  (Object.keys(next) as HireInboxItemKey[]).forEach((key) => {
    const diff = next[key] - previous[key];
    if (diff > 0) delta[key] = diff;
  });
  return delta;
}
